import React from 'react';

const SLIDES = [
  { glyph: '✦', title: 'Meet Mira', body: "Xin's resident thinking partner. She turns table-side sparks into ideas worth keeping." },
  { glyph: '◍', title: 'Bring anything', body: 'A problem, a possibility, or a completely blank mind. Mira finds the thread and pulls.' },
  { glyph: '🎙', title: 'Talk or type', body: 'Tap the mic and think out loud — she listens. Hand her a photo, a PDF or a spreadsheet too.' },
  { glyph: '❖', title: 'Land the idea', body: 'When it clicks, Mira shapes it into a Spark Card — your idea, printed on the Canvas wall.' },
  { glyph: '∞', title: 'She remembers', body: 'Come back next visit and Mira picks up right where you left off. Your thinking, kept.' },
];
const SLIDE_MS = 4000;

let injected = false;
function injectStyles() {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const el = document.createElement('style');
  el.textContent = `
    @keyframes miraObFade { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes miraObGlow { 0%,100% { text-shadow: 0 0 22px rgba(232,184,75,0.55); transform: scale(1); } 50% { text-shadow: 0 0 44px rgba(232,184,75,0.9); transform: scale(1.08); } }
  `;
  document.head.appendChild(el);
}

export default function MiraOnboarding({ onDone }) {
  injectStyles();
  const [idx, setIdx] = React.useState(0);
  const done = React.useCallback(() => {
    try { window.localStorage.setItem('mira:onboarded_v1', '1'); } catch { /* no-op */ }
    onDone && onDone();
  }, [onDone]);

  React.useEffect(() => {
    if (idx >= SLIDES.length - 1) return undefined;
    const t = setTimeout(() => setIdx((i) => i + 1), SLIDE_MS);
    return () => clearTimeout(t);
  }, [idx]);

  const last = idx === SLIDES.length - 1;
  const s = SLIDES[idx];

  return (
    <div data-testid="mira-onboarding" style={{
      position: 'fixed', inset: 0, zIndex: 4000,
      background: 'radial-gradient(120% 120% at 50% 0%, #3A1140 0%, #211826 55%, #150F1A 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'system-ui, -apple-system, sans-serif', padding: 24,
    }}>
      {/* progress dots */}
      <div style={{ position: 'absolute', top: 26, display: 'flex', gap: 8 }}>
        {SLIDES.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} aria-label={`Step ${i + 1}`} style={{
            width: i === idx ? 26 : 8, height: 8, borderRadius: 99, border: 'none', cursor: 'pointer',
            background: i <= idx ? '#E8B84B' : '#4A3552', transition: 'width 300ms ease, background-color 300ms ease',
            padding: 0,
          }} />
        ))}
      </div>

      <button onClick={done} data-testid="mira-onboarding-skip" style={{
        position: 'absolute', top: 20, right: 22, background: 'none',
        border: '1px solid #4A3552', color: '#CBB8D0', borderRadius: 999,
        padding: '6px 16px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
      }}>Skip</button>

      <div key={idx} style={{ textAlign: 'center', maxWidth: 420, animation: 'miraObFade 600ms ease both' }}>
        <div style={{ fontSize: 54, color: '#E8B84B', animation: 'miraObGlow 2.6s ease-in-out infinite', lineHeight: 1 }}>
          {s.glyph}
        </div>
        <div style={{ color: '#F5EFE8', fontWeight: 900, fontSize: 28, marginTop: 22, letterSpacing: 0.3 }}>
          {s.title}
        </div>
        <div style={{ color: '#CBB8D0', fontSize: 15, lineHeight: 1.7, marginTop: 12 }}>
          {s.body}
        </div>
        {last ? (
          <button onClick={done} data-testid="mira-onboarding-start" style={{
            marginTop: 30, background: '#E8B84B', color: '#3A1140', border: 'none',
            borderRadius: 999, padding: '13px 34px', fontWeight: 900, fontSize: 14,
            cursor: 'pointer', letterSpacing: 0.4, boxShadow: '0 10px 30px rgba(232,184,75,0.3)',
          }}>Start thinking ✦</button>
        ) : (
          <button onClick={() => setIdx((i) => i + 1)} data-testid="mira-onboarding-next" style={{
            marginTop: 30, background: 'transparent', color: '#E8B84B',
            border: '1px solid rgba(232,184,75,0.45)', borderRadius: 999,
            padding: '11px 28px', fontWeight: 900, fontSize: 13, cursor: 'pointer',
          }}>Next →</button>
        )}
      </div>

      <div style={{ position: 'absolute', bottom: 24, color: '#6B5573', fontSize: 11 }}>
        Mira · the room where ideas eat
      </div>
    </div>
  );
}
